import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Progress } from '../../../components/ui/progress';
import { TrendingUp, TrendingDown, Calendar, Award, DollarSign, Users } from 'lucide-react';
import { PromotionStats, Promotion } from '../types';

interface PromotionsDashboardProps {
  stats: PromotionStats;
  promotions: Promotion[];
}

interface MetricCard {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ReactNode;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  color?: string;
}

export const PromotionsDashboard: React.FC<PromotionsDashboardProps> = ({
  stats,
  promotions
}) => {
  // Calcular métricas adicionales usando isActive y fechas
  const getPromotionStatus = (promotion: Promotion) => {
    const now = new Date();
    if (!promotion.isActive) return 'inactive';
    if (promotion.endDate && now > new Date(promotion.endDate)) return 'expired';
    if (promotion.startDate && now < new Date(promotion.startDate)) return 'scheduled';
    return 'active';
  };

  const activePromotions = promotions.filter(p => getPromotionStatus(p) === 'active');
  const scheduledPromotions = promotions.filter(p => getPromotionStatus(p) === 'scheduled');
  const averageUsage = stats.metrics.totalPromotions > 0
    ? promotions.reduce((sum, p) => sum + p.usageCount, 0) / stats.metrics.totalPromotions
    : 0;
  
  const topPromotions = promotions
    .sort((a, b) => b.usageCount - a.usageCount)
    .slice(0, 5);

  const promotionsByType = promotions.reduce((acc, promotion) => {
    acc[promotion.type] = (acc[promotion.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const metrics: MetricCard[] = [
    {
      title: 'Total Promociones',
      value: stats.metrics.totalPromotions,
      subtitle: `${activePromotions.length} activas`,
      icon: <Award className="h-5 w-5" />,
      color: 'text-blue-600'
    },
    {
      title: 'Ahorros Generados',
      value: `S/ ${(stats.metrics.totalSavings || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 })}`,
      subtitle: 'Total estimado',
      icon: <DollarSign className="h-5 w-5" />,
      trend: {
        value: 12.5,
        isPositive: true
      },
      color: 'text-green-600'
    },
    {
      title: 'Promociones Activas',
      value: activePromotions.length,
      subtitle: `${scheduledPromotions.length} programadas`,
      icon: <TrendingUp className="h-5 w-5" />,
      color: 'text-purple-600'
    },
    {
      title: 'Uso Promedio',
      value: stats.metrics.averageUsage,
      subtitle: 'usos por promoción',
      icon: <Users className="h-5 w-5" />,
      color: 'text-orange-600'
    }
  ];

  const getTypeLabel = (type: string) => {
    const labels = {
      percentage: 'Porcentual',
      fixed: 'Fijo',
      combo: 'Combo',
      bxgy: 'Compra X Lleva Y'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getStatusColor = (status: string) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      expired: 'bg-red-100 text-red-800'
    };
    return colors[status as keyof typeof colors] || colors.inactive;
  };

  return (
    <div className="space-y-6">
      {/* Métricas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, index) => (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{metric.title}</CardTitle>
              <div className={metric.color}>
                {metric.icon}
              </div>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${metric.color}`}>{metric.value}</div>
              {metric.subtitle && (
                <p className="text-xs text-muted-foreground mt-1">
                  {metric.subtitle}
                </p>
              )}
              {metric.trend && (
                <div className="flex items-center mt-2">
                  {metric.trend.isPositive ? (
                    <TrendingUp className="h-3 w-3 text-green-500 mr-1" />
                  ) : (
                    <TrendingDown className="h-3 w-3 text-red-500 mr-1" />
                  )}
                  <span className={`text-xs ${
                    metric.trend.isPositive ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {metric.trend.value}%
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Promociones */}
        <Card>
          <CardHeader>
            <CardTitle>Promociones Más Utilizadas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {topPromotions.map((promotion, index) => {
                const maxUsage = Math.max(...topPromotions.map(p => p.usageCount));
                const percentage = maxUsage > 0 ? (promotion.usageCount / maxUsage) * 100 : 0;
                
                return (
                  <div key={promotion.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="text-sm font-medium">#{index + 1}</span>
                        <span className="text-sm truncate">{promotion.name}</span>
                        <Badge className={getStatusColor(getPromotionStatus(promotion))}>
                          {getPromotionStatus(promotion) === 'active' ? 'Activo' :
                           getPromotionStatus(promotion) === 'inactive' ? 'Inactivo' :
                           getPromotionStatus(promotion) === 'scheduled' ? 'Programado' : 'Expirado'}
                        </Badge>
                      </div>
                      <span className="text-sm font-semibold">{promotion.usageCount}</span>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Distribución por Tipo */}
        <Card>
          <CardHeader>
            <CardTitle>Distribución por Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(promotionsByType).map(([type, count]) => {
                const percentage = (count / stats.metrics.totalPromotions) * 100;
                
                return (
                  <div key={type} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{getTypeLabel(type)}</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-sm text-muted-foreground">
                          {percentage.toFixed(1)}%
                        </span>
                        <span className="text-sm font-semibold">{count}</span>
                      </div>
                    </div>
                    <Progress value={percentage} className="h-2" />
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Promociones Top desde la API */}
      <Card>
        <CardHeader>
          <CardTitle>Impacto y Rendimiento por Promoción</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {stats.topPromotions.map((promotion) => {
                return (
                  <div key={promotion.id} className="flex items-center justify-between py-2">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium truncate max-w-xs">
                          {promotion.name}
                        </span>
                        <div className="flex items-center space-x-2">
                          <span className="text-sm font-semibold text-blue-600">
                            {promotion.usageCount} usos
                          </span>
                          <Badge variant="outline">
                            {getTypeLabel(promotion.type)}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Progress value={promotion.usagePercentage} className="h-1.5 flex-1" />
                        <span className="text-xs text-muted-foreground">
                          {promotion.usagePercentage}%
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};